import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuIcon, XIcon, PhoneIcon, MailIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";

// [Branding] [Conversion] Header → Bricks header template.
const LOGO_SRC = "https://cdn.magicpatterns.com/uploads/wfGFQpbMXGFxGofAbTAYd5/Oceanside_logo_transparent_PNG.png";
const navItems = [{
  label: "Home Designs",
  to: "/home-designs"
}, {
  label: "Our Work",
  to: "/projects"
}, {
  label: "What We Build",
  to: "/#services"
}, {
  label: "Our Process",
  to: "/our-process"
}, {
  label: "About",
  to: "/about"
}, {
  label: "Contact",
  to: "/contact"
}];
export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash, location.search]);
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const isActive = (to: string) => !to.includes("#") && location.pathname.startsWith(to);
  return <header className="sticky top-0 z-40 border-b border-line bg-paper" data-id="element-63" data-mp="3k4jjajxkye5i">
      <div className="mx-auto flex h-[76px] max-w-site items-center justify-between gap-4 px-5 md:px-10" data-id="element-64" data-mp="3p1jrp1hwjszs">
        <Link to="/" aria-label="Oceanside Living home" className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal" data-id="element-65" data-mp="3u2h93bv38j08">
          <img src={LOGO_SRC} alt="Oceanside Living" width={2576} height={1394} className="h-11 w-auto md:h-12" data-id="element-66" data-mp="19axctuibnn87" />
        </Link>

        <nav aria-label="Main" className="hidden xl:block" data-id="element-67" data-mp="1da65sx5jfh2p">
          <ul className="flex items-center gap-7" data-id="element-68" data-mp="3ablpiu0jrgjo">
            {navItems.map(item => <li key={item.label} data-id="element-69" data-mp="35dtlx2qji5zn">
                <Link to={item.to} aria-current={isActive(item.to) ? "page" : undefined} className={`whitespace-nowrap text-[15px] font-medium text-charcoal underline-offset-[6px] hover:underline ${isActive(item.to) ? "underline decoration-ocean decoration-2" : ""}`} data-id="element-70" data-mp="35csauy9ixjhx">
                  {item.label}
                </Link>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-1 sm:gap-3" data-id="element-71" data-mp="30ez526c4uri6">
          <a href="tel:+61862850876" className="hidden items-center gap-2 whitespace-nowrap text-[15px] font-medium text-charcoal underline-offset-4 hover:underline xl:inline-flex" data-id="element-72" data-mp="2vnquv82zb23y">
            <PhoneIcon className="h-4 w-4 text-slate" aria-hidden="true" data-id="element-73" data-mp="22zl9qk80jb3l" />
            08 6285 0876
          </a>
          <a href="tel:+61862850876" aria-label="Call Oceanside Living on 08 6285 0876" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-charcoal transition-colors duration-150 ease-out hover:bg-paper-deep xl:hidden" data-id="element-74" data-mp="ifzc9xvg4uwj">
            <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-75" data-mp="eg8pe591iyxa" />
          </a>
          <ButtonLink to="/contact" className="hidden sm:inline-flex" data-id="element-76" data-mp="1248hp9m02xdz">
            Book a consultation
          </ButtonLink>
          <ButtonLink to="/contact" className="!min-h-[44px] !px-3.5 sm:hidden" ariaLabel="Book a consultation" data-id="element-77" data-mp="16524fznpi65a">
            Book
          </ButtonLink>
          <button ref={toggleRef} type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="mobile-menu" className="inline-flex h-11 w-11 items-center justify-center rounded-full text-charcoal transition-colors duration-150 ease-out hover:bg-paper-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal xl:hidden" data-id="element-78" data-mp="x5orgx0dizft">
            {open ? <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-79" data-mp="3sdjeh9a6kxqy" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-80" data-mp="3ocpq6esttgxj" />}
            <span className="sr-only" data-id="element-81" data-mp="3oav3auxzveqt">{open ? "Close menu" : "Open menu"}</span>
          </button>
        </div>
      </div>

      {open && <div id="mobile-menu" ref={menuRef} className="border-t border-line bg-paper xl:hidden" data-id="element-82" data-mp="jzaswl62t78a">
          <nav aria-label="Mobile" className="mx-auto max-w-site px-5 py-4 md:px-10" data-id="element-83" data-mp="fx5yvlbpu8l4">
            <ul className="divide-y divide-line" data-id="element-84" data-mp="axtdz64enbl6">
              {navItems.map(item => <li key={item.label} data-id="element-85" data-mp="5zi2kqygot0u">
                  <Link to={item.to} className="flex min-h-[52px] items-center font-heading text-lg font-semibold text-charcoal" data-id="element-86" data-mp="5yghdtnqeq5e">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
            <div className="mt-4 flex flex-col gap-3 pb-2" data-id="element-87" data-mp="2qrto71e8eqap">
              <ButtonLink to="/contact" data-id="element-88" data-mp="2gmv870ayyatp">Book a consultation</ButtonLink>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-[15px]" data-id="element-89" data-mp="2ljty16nlnvm3">
                <a href="tel:+61862850876" className="inline-flex items-center gap-2 underline underline-offset-4" data-id="element-90" data-mp="2qjpzay4wx8xy">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-91" data-mp="2vayekasxboj1" /> 08 6285 0876
                </a>
                <a href="mailto:admin@oceansideliving.com.au" className="inline-flex items-center gap-2 underline underline-offset-4" data-id="element-92" data-mp="2zcjnxm691koh">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-93" data-mp="349jrnqfmmbcf" /> admin@oceansideliving.com.au
                </a>
              </div>
            </div>
          </nav>
        </div>}
    </header>;
}